import React from 'react';
import { Monitor } from 'lucide-react';

/**
 * DevOnlyGuard - Shows info banner when dev-only pages are accessed outside dev VMs.
 * Used as an early return in development pages, so the whole section is blocked rather
 * than the controls being left reachable alongside a warning.
 *
 * `message` overrides the default explanation. The original callers are pages that read
 * local files; the sync tools are dev-only for a different reason (the cross-environment
 * connection strings live only in appsettings.Development.json), and saying "reads local
 * files" there would be wrong.
 */
const DevOnlyGuard = ({ pageName, message }) => {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      padding: '2rem'
    }}>
      <div style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '2.5rem',
        maxWidth: '480px',
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <Monitor size={48} style={{ color: '#64748b', marginBottom: '1rem' }} />
        <h2 style={{ margin: '0 0 0.75rem', color: '#1e293b', fontSize: '1.25rem' }}>
          {pageName || 'Development Tool'} — Dev VMs Only
        </h2>
        <p style={{ margin: 0, color: '#64748b', lineHeight: 1.6 }}>
          {message ||
            'This page reads local files that are only available on development VMs. ' +
            'It is not accessible in the Azure environment.'}
        </p>
      </div>
    </div>
  );
};

/**
 * HOC wrapper — gates a component behind IS_DEV_VM check.
 * Usage: export default withDevGuard(MyPage, 'My Page');
 */
export const withDevGuard = (Component, pageName) => {
  return function DevGuardedComponent(props) {
    if (!window.__config__?.IS_DEV_VM) return <DevOnlyGuard pageName={pageName} />;
    return <Component {...props} />;
  };
};

export default DevOnlyGuard;
